import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import pick from 'lodash/pick';
import { createBooking, cancelBooking, initiatePrivileged, transitionPrivileged } from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { setCurrentUserHasOrders, fetchCurrentUser } from '../../ducks/user.duck';
import moment from 'moment-timezone';
// ================ Async thunks ================ //
////////////////////
// Initiate Order //
////////////////////
/**
 * Match a specific start time against listing availability patterns to get totalSeats
 * (Duplicate of backend logic to ensure frontend passes correct data)
 */
const getDynamicTotalSeats = (listing, startAt) => {
  const publicData = listing?.attributes?.publicData || {};
  const { recurringSlotWeekly, nonRecurringSlots, timeZone: listingTZRoot } = publicData;
  const availabilityPlanTZ = listing?.attributes?.availabilityPlan?.timezone;
  const startMoment = moment(startAt);
  // 1. Check nonRecurringSlots (Exact Date/Time matching)
  if (nonRecurringSlots && Array.isArray(nonRecurringSlots)) {
    for (const slot of nonRecurringSlots) {
      const patternStart = moment(slot.startDate * 1000);
      const isMatch = patternStart.isSame(startMoment, 'minute');
      if (isMatch) return slot.seats || 1;
    }
  }
  // 2. Check recurringSlotWeekly (Day of week + Time matching)
  if (recurringSlotWeekly && Array.isArray(recurringSlotWeekly)) {
    for (const pattern of recurringSlotWeekly) {
      // Priority: Pattern-level TZ > Listing-level TZ > AvailabilityPlan TZ > UTC
      const targetTZ = pattern.timeZone || listingTZRoot || availabilityPlanTZ || 'UTC';
      const localStart = startMoment.clone().tz(targetTZ);
      const dayOfWeek = localStart.format('dddd');
      const startTimeStr = localStart.format('HH:mm');
      if (pattern.day === dayOfWeek && pattern.startTime === startTimeStr) {
        return pattern.seats || 1;
      }
    }
  }
  return publicData.seats || 1;
};
const initiateOrderPayloadCreator = (
  { orderParams, processAlias, transactionId, transitionName, isPrivilegedTransition },
  { dispatch, extra: sdk, rejectWithValue, getState }
) => {
  // If we already have a transaction ID, we should transition, not initiate.
  const isTransition = !!transactionId;
  const {
    deliveryMethod,
    priceVariantName,
    quantity,
    seats,
    bookingDates,
    protectedData,
    ...otherOrderParams
  } = orderParams;

  // Extract MongoDB variant data from protectedData if available
  const mongoVariantData = protectedData ? {
    ...(protectedData.variantName ? { variantName: protectedData.variantName } : {}),
    ...(protectedData.variantPrice ? { variantPrice: protectedData.variantPrice } : {}),
    ...(protectedData.sessionLength ? { sessionLength: protectedData.sessionLength } : {}),
    ...(protectedData.slotId ? { slotId: protectedData.slotId } : {}),
    ...(protectedData.currency ? { currency: protectedData.currency } : {}),
  } : {};

  // Detect custom slot listing (tour-activity, tour-activity-multi) from protectedData markers
  const isCustomSlot = !!(protectedData?.slotId || protectedData?.variantName);

  // For custom slot listings, pass `seats` as a booking parameter so Sharetribe
  // correctly deducts from the availability exception's seat count.
  // For non-slot listings (products/purchases), use `stockReservationQuantity`.
  const quantityMaybe = isCustomSlot
    ? (seats ? { seats } : {})
    : quantity
      ? { stockReservationQuantity: quantity }
      : seats
        ? { stockReservationQuantity: seats }
        : {};
  const bookingParamsMaybe = bookingDates || {};
  // Extract booking dates from orderParams
  const bookingStartMaybe = otherOrderParams.bookingStart ? { bookingStart: otherOrderParams.bookingStart } : {};
  const bookingEndMaybe = otherOrderParams.bookingEnd ? { bookingEnd: otherOrderParams.bookingEnd } : {};

  // Parameters only for client app's server
  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(priceVariantName ? { priceVariantName } : {}),
    ...mongoVariantData, // Include MongoDB variant data
    ...bookingStartMaybe, // Include booking start
    ...bookingEndMaybe, // Include booking end
  };
  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    protectedData,
  };
  const bodyParams = isTransition
    ? {
      id: transactionId,
      transition: transitionName,
      params: transitionParams,
    }
    : {
      processAlias,
      transition: transitionName,
      params: transitionParams,
    };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };
  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(setCurrentUserHasOrders());
    return order;
  };
  const handleError = e => {
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: orderParams?.listingId?.uuid || orderParams?.listingId,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
      statusText: e.statusText,
    });
    return rejectWithValue(storableError(e));
  };
  // MongoDB createBooking integration
  // For new initiations, we call createBooking AFTER Sharetribe initiation
  // to capture the generated txId. For transitions, we call it BEFORE.
  const state = getState();
  const listingIdStr =
    orderParams?.listingId?.uuid ||
    (typeof orderParams?.listingId === 'string'
      ? orderParams.listingId
      : orderParams?.listingId?.toString?.());
  const listing =
    state.marketplaceData?.entities?.listing?.[listingIdStr] ||
    state.marketplaceData?.entities?.ownListing?.[listingIdStr];
  const currentUser = state.user?.currentUser;

  const usesMongoDBSlots = !!(
    listing?.attributes?.publicData?.usesMongoDBSlots ||
    protectedData?.slotId ||
    protectedData?.variantName
  );

  const mongoBookingData = {
    txId: transactionId ? transactionId.uuid || transactionId : null,
    listingId: listingIdStr,
    authorId: listing?.attributes?.authorId?.uuid || listing?.author?.id?.uuid,
    customerId: currentUser?.id?.uuid,
    startAt: orderParams.bookingStart,
    endAt: orderParams.bookingEnd,
    timezone: listing?.attributes?.availabilityPlan?.timezone,
    seatsBooked: orderParams.seats || 1,
    totalSeats: getDynamicTotalSeats(listing, orderParams.bookingStart),
    metadata: orderParams.protectedData,
    /* The listing as the guest is looking at it, so that booking it puts it on their
       itinerary as saving it would. The server has no listing of its own to put there —
       the itinerary stores the snapshot, and this is the page that has one. It is the
       same entity the save button on a listing sends, out of the same place, so a guest
       who saved it first and a guest who only booked it end up with the same entry. */
    listingTitle: listing?.attributes?.title,
    listing,
  };
  const performStripeAction = () => {
    if (isTransition && isPrivilegedTransition) {
      // transition privileged
      return transitionPrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
        .then(handleSuccess)
        .catch(handleError);
    } else if (isTransition) {
      // transition non-privileged
      return sdk.transactions
        .transition(bodyParams, queryParams)
        .then(handleSuccess)
        .catch(handleError);
    } else if (isPrivilegedTransition) {
      // initiate privileged
      return initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
        .then(handleSuccess)
        .catch(handleError);
    } else {
      // initiate non-privileged
      return sdk.transactions
        .initiate(bodyParams, queryParams)
        .then(handleSuccess)
        .catch(handleError);
    }
  };

  /**
   * A listing that keeps its availability in Sharetribe.
   *
   * This used to go straight to Sharetribe and write nothing of its own, and so the
   * guest booked, paid, and found their itinerary still offering them the date: the
   * itinerary is drawn from our own records, and there was no record. If they had saved
   * the experience first, the card sat there saying "Book now" after the money had gone.
   *
   * So the booking is written down — but after the order rather than before it, and
   * never at its expense. The other branches put the write first because the write is a
   * seat reservation and must beat the next guest to it; here there is nothing of ours
   * to reserve, Sharetribe holds the availability, and the row is a record. A record
   * worth having is not worth losing a sale over, so a failure to write it is logged and
   * the order stands — the same rule the itinerary entry is written under.
   *
   * The txId goes in because that is what the event cron matches on when the provider
   * accepts, declines or cancels; see deafultBookingTransactionEvent.
   */
  if (!usesMongoDBSlots) {
    return performStripeAction().then(order => {
      const txId = order?.id?.uuid;
      if (!txId) return order;
      return createBooking({ ...mongoBookingData, txId, origin: 'sharetribe' })
        .catch(e =>
          console.error('Order placed, but could not record the booking for the itinerary:', e)
        )
        .then(() => order);
    });
  }

  if (isTransition) {
    // For transitions, we already have the txId — create booking then transition
    return createBooking(mongoBookingData)
      .then(response => {
        const mongoBookingId = response.booking?.bookingId;
        return performStripeAction()
          .catch(sdkError => {
            // Sharetribe transition failed — release the seats we reserved
            console.warn('Sharetribe transition failed, releasing MongoDB seats...');
            if (mongoBookingId) {
              cancelBooking({ bookingId: mongoBookingId }).catch(e =>
                console.error('Cleanup: Failed to release seats after Sharetribe transition failure', e)
              );
            }
            return handleError(sdkError);
          });
      })
      .catch(mongoError => {
        console.error('MongoDB Booking Creation Failed:', mongoError);
        return rejectWithValue(storableError(mongoError));
      });
  } else {
    // For new initiations, we now call createBooking BEFORE Sharetribe initiation
    // to ensure seats are reserved before the Sharetribe transaction is even created.
    return createBooking(mongoBookingData)
      .then(response => {
        const mongoBookingId = response.booking?.bookingId;

        return performStripeAction()
          .then(order => {
            const txId = order.id.uuid;
            // Update the MongoDB booking with the actual txId from Sharetribe
            // We can call confirmBooking with txId here just to update the txId field
            return createBooking({ ...mongoBookingData, txId, bookingId: mongoBookingId })
              .then(() => order)
              .catch(updateError => {
                console.error('Failed to update MongoDB booking with TxID:', updateError);
                return order; // Still return order as the seats ARE reserved
              });
          })
          .catch(sdkError => {
            // Sharetribe initiation failed - RELEASE the seats we pre-reserved
            console.warn('Sharetribe initiation failed, releasing MongoDB seats...');
            if (mongoBookingId) {
              cancelBooking({ bookingId: mongoBookingId }).catch(e =>
                console.error('Cleanup: Failed to release seats after Sharetribe failure', e)
              );
            }
            return handleError(sdkError);
          });
      })
      .catch(mongoError => {
        console.error('MongoDB Seat Reservation Failed:', mongoError);
        return rejectWithValue(storableError(mongoError));
      });
  }
};
export const initiateOrderThunk = createAsyncThunk(
  'CheckoutPage/initiateOrder',
  initiateOrderPayloadCreator
);
// Backward compatible wrapper function for initiateOrder
export const initiateOrder = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => dispatch => {
  return dispatch(
    initiateOrderThunk({
      orderParams,
      processAlias,
      transactionId,
      transitionName,
      isPrivilegedTransition,
    })
  ).unwrap();
};
/////////////////////
// Confirm Payment //
/////////////////////
const confirmPaymentPayloadCreator = (
  { transactionId, transitionName, transitionParams = {} },
  { extra: sdk, rejectWithValue }
) => {
  const bodyParams = {
    id: transactionId,
    transition: transitionName,
    params: transitionParams,
  };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };
  return sdk.transactions
    .transition(bodyParams, queryParams)
    .then(response => {
      const order = response.data.data;
      return order;
    })
    .catch(e => {
      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
      });
      return rejectWithValue(storableError(e));
    });
};
export const confirmPaymentThunk = createAsyncThunk(
  'CheckoutPage/confirmPayment',
  confirmPaymentPayloadCreator
);
// Backward compatible wrapper function for confirmPayment
export const confirmPayment = (
  transactionId,
  transitionName,
  transitionParams = {}
) => dispatch => {
  return dispatch(
    confirmPaymentThunk({
      transactionId,
      transitionName,
      transitionParams,
    })
  ).unwrap();
};
//////////////////
// Send Message //
//////////////////
const sendMessagePayloadCreator = ({ message, id: orderId }, { extra: sdk, rejectWithValue }) => {
  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return rejectWithValue({ orderId, messageSuccess: false });
      });
  } else {
    return { orderId, messageSuccess: true };
  }
};
export const sendMessageThunk = createAsyncThunk(
  'CheckoutPage/sendMessage',
  sendMessagePayloadCreator
);
// Backward compatible wrapper function for sendMessage
export const sendMessage = params => dispatch => {
  return dispatch(sendMessageThunk(params)).unwrap();
};
//////////////////////
// Initiate Inquiry //
//////////////////////
const initiateInquiryPayloadCreator = (
  { inquiryParams, processAlias, transitionName },
  { extra: sdk, rejectWithValue }
) => {
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: inquiryParams?.listingId?.uuid,
    });
    return rejectWithValue(storableError(error));
  }
  const bodyParams = {
    transition: transitionName,
    processAlias,
    params: inquiryParams,
  };
  const queryParams = {
    include: ['provider'],
    expand: true,
  };
  return sdk.transactions
    .initiate(bodyParams, queryParams)
    .then(response => {
      const transactionId = response.data.data.id;
      return transactionId;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};
export const initiateInquiryThunk = createAsyncThunk(
  'CheckoutPage/initiateInquiry',
  initiateInquiryPayloadCreator
);
// Backward compatible wrapper function for initiateInquiryWithoutPayment
/**
 * Initiate transaction against default-inquiry process
 * Note: At this point inquiry transition is made directly against Marketplace API.
 *       So, client app's server is not involved here unlike with transitions including payments.
 *
 * @param {Object} params
 * @param {Object} params.inquiryParams contains listingId and protectedData
 * @param {String} params.processAlias 'default-inquiry/release-1'
 * @param {String} params.transitionName 'transition/inquire-without-payment'
 * @returns
 */
export const initiateInquiryWithoutPayment = (
  inquiryParams,
  processAlias,
  transitionName
) => dispatch => {
  return dispatch(
    initiateInquiryThunk({
      inquiryParams,
      processAlias,
      transitionName,
    })
  ).unwrap();
};
///////////////////////////
// Speculate Transaction //
///////////////////////////
/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */
const speculateTransactionPayloadCreator = (
  { orderParams, processAlias, transactionId, transitionName, isPrivilegedTransition },
  { dispatch, extra: sdk, rejectWithValue }
) => {
  // If we already have a transaction ID, we should transition, not initiate.
  const isTransition = !!transactionId;
  const {
    deliveryMethod,
    priceVariantName,
    quantity,
    seats,
    bookingDates,
    protectedData,
    ...otherOrderParams
  } = orderParams;

  // Extract MongoDB variant data from protectedData if available
  const mongoVariantData = protectedData ? {
    ...(protectedData.variantName ? { variantName: protectedData.variantName } : {}),
    ...(protectedData.variantPrice ? { variantPrice: protectedData.variantPrice } : {}),
    ...(protectedData.sessionLength ? { sessionLength: protectedData.sessionLength } : {}),
    ...(protectedData.slotId ? { slotId: protectedData.slotId } : {}),
    ...(protectedData.currency ? { currency: protectedData.currency } : {}),
  } : {};

  // Detect custom slot listing (tour-activity, tour-activity-multi) from protectedData markers
  const isCustomSlot = !!(protectedData?.slotId || protectedData?.variantName);

  // For custom slot listings, pass `seats` as a booking parameter so Sharetribe
  // correctly deducts from the availability exception's seat count.
  // For non-slot listings (products/purchases), use `stockReservationQuantity`.
  const quantityMaybe = isCustomSlot
    ? (seats ? { seats } : {})
    : quantity
      ? { stockReservationQuantity: quantity }
      : seats
        ? { stockReservationQuantity: seats }
        : {};
  const bookingParamsMaybe = bookingDates || {};
  // Extract booking dates from orderParams
  const bookingStartMaybe = otherOrderParams.bookingStart ? { bookingStart: otherOrderParams.bookingStart } : {};
  const bookingEndMaybe = otherOrderParams.bookingEnd ? { bookingEnd: otherOrderParams.bookingEnd } : {};

  // Parameters only for client app's server
  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(priceVariantName ? { priceVariantName } : {}),
    ...mongoVariantData, // Include MongoDB variant data
    ...bookingStartMaybe, // Include booking start
    ...bookingEndMaybe, // Include booking end
  };
  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    protectedData,
    cardToken: 'CheckoutPage_speculative_card_token',
  };
  const bodyParams = isTransition
    ? {
      id: transactionId,
      transition: transitionName,
      params: transitionParams,
    }
    : {
      processAlias,
      transition: transitionName,
      params: transitionParams,
    };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };
  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    return tx;
  };
  const handleError = e => {
    log.error(e, 'speculate-transaction-failed', {
      listingId: transitionParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
      statusText: e.statusText,
    });
    return rejectWithValue(storableError(e));
  };
  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};
export const speculateTransactionThunk = createAsyncThunk(
  'CheckoutPage/speculateTransaction',
  speculateTransactionPayloadCreator
);
// Backward compatible wrapper function for speculateTransaction
export const speculateTransaction = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => dispatch => {
  return dispatch(
    speculateTransactionThunk({
      orderParams,
      processAlias,
      transactionId,
      transitionName,
      isPrivilegedTransition,
    })
  ).unwrap();
};
///////////////////////////
// Fetch Stripe Customer //
///////////////////////////
const stripeCustomerPayloadCreator = ({ }, { dispatch, rejectWithValue }) => {
  const fetchCurrentUserOptions = {
    callParams: { include: ['stripeCustomer.defaultPaymentMethod'] },
    updateHasListings: false,
    updateNotifications: false,
    enforce: true,
  };
  return dispatch(fetchCurrentUser(fetchCurrentUserOptions))
    .then(response => {
      return response;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};
export const stripeCustomerThunk = createAsyncThunk(
  'CheckoutPage/stripeCustomer',
  stripeCustomerPayloadCreator
);
// Backward compatible wrapper function for stripeCustomer
export const stripeCustomer = () => dispatch => {
  return dispatch(stripeCustomerThunk({})).unwrap();
};
// ================ Slice ================ //
const initialState = {
  listing: null,
  orderData: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  isClockInSync: false,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  initiateInquiryInProgress: false,
  initiateInquiryError: null,
};
const checkoutPageSlice = createSlice({
  name: 'CheckoutPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...pick(action.payload, Object.keys(initialState)) };
    },
  },
  extraReducers: builder => {
    builder
      // Initiate Order cases
      .addCase(initiateOrderThunk.pending, state => {
        state.initiateOrderError = null;
      })
      .addCase(initiateOrderThunk.fulfilled, (state, action) => {
        state.transaction = action.payload;
      })
      .addCase(initiateOrderThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.initiateOrderError = action.payload;
      })
      // Confirm Payment cases
      .addCase(confirmPaymentThunk.pending, state => {
        state.confirmPaymentError = null;
      })
      .addCase(confirmPaymentThunk.fulfilled, state => {
        // Payment confirmed successfully, no state change needed
      })
      .addCase(confirmPaymentThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.confirmPaymentError = action.payload;
      })
      // Speculate Transaction cases
      .addCase(speculateTransactionThunk.pending, state => {
        state.speculateTransactionInProgress = true;
        state.speculateTransactionError = null;
        state.speculatedTransaction = null;
      })
      .addCase(speculateTransactionThunk.fulfilled, (state, action) => {
        // Check that the local devices clock is within a minute from the server
        const lastTransitionedAt = action.payload?.attributes?.lastTransitionedAt;
        const localTime = new Date();
        const minute = 60000;
        state.speculateTransactionInProgress = false;
        state.speculatedTransaction = action.payload;
        state.isClockInSync =
          Math.abs(lastTransitionedAt?.getTime() - localTime.getTime()) < minute;
      })
      .addCase(speculateTransactionThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.speculateTransactionInProgress = false;
        state.speculateTransactionError = action.payload;
      })
      // Stripe Customer cases
      .addCase(stripeCustomerThunk.pending, state => {
        state.stripeCustomerFetched = false;
      })
      .addCase(stripeCustomerThunk.fulfilled, state => {
        state.stripeCustomerFetched = true;
      })
      .addCase(stripeCustomerThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.stripeCustomerFetchError = action.payload;
      })
      // Initiate Inquiry cases
      .addCase(initiateInquiryThunk.pending, state => {
        state.initiateInquiryInProgress = true;
        state.initiateInquiryError = null;
      })
      .addCase(initiateInquiryThunk.fulfilled, state => {
        state.initiateInquiryInProgress = false;
      })
      .addCase(initiateInquiryThunk.rejected, (state, action) => {
        state.initiateInquiryInProgress = false;
        state.initiateInquiryError = action.payload;
      });
  },
});
// Export the action creators
export const { setInitialValues } = checkoutPageSlice.actions;
// Export the reducer
export default checkoutPageSlice.reducer;